import { InfiniteData, QueryClient, useInfiniteQuery } from '@tanstack/react-query';

import { useSearchNonF2fDoctorsForSeoByUserQuery } from '@/hooks/nonBenefit/nonBenefit.gql.impl';
import { NonBenefitNonF2fDoctor } from '@/models/nonBenefit';
import { SearchNonF2fDoctorsForSeoByUserQuery } from '@/types/bff/gql-schema';
import { DiagnosisContactType, NonF2fDoctorSeoSearchRequestInput } from '@/types/bff/gql-schema';

const parseSearchNonF2fDoctorsForSeoByUser = (
  data: InfiniteData<SearchNonF2fDoctorsForSeoByUserQuery>
): InfiniteData<NonBenefitNonF2fDoctor[]> => {
  return {
    ...data,
    pages: data.pages.map((page) => {
      return page.searchNonF2fDoctorsForSeoByUser.map((doctor) => {
        return new NonBenefitNonF2fDoctor({
          doctorId: doctor.doctorId,
          name: doctor.name,
          medicineType: doctor.medicineType,
          hospitalName: doctor.hospital.hospitalName,
          publicId: doctor.publicId,
          hospitalAddress: {
            jibunAddress: doctor.hospital.hospitalAddress.jibunAddress ?? '',
            roadAddress: doctor.hospital.hospitalAddress.roadAddress ?? '',
            regionByDepth: {
              siDoName: doctor.hospital.hospitalAddress.regionByDepth.siDoName,
              siGunGuName: doctor.hospital.hospitalAddress.regionByDepth.siGunGuName ?? '',
              legalEupMyeonDongName: doctor.hospital.hospitalAddress.regionByDepth.legalEupMyeonDongName,
            },
          },
          nonBenefitTreatments: doctor.nonBenefitTreatmentsV2.map((treatment) => ({
            nonBenefitTreatmentUnitInfos: treatment.nonBenefitTreatmentUnitInfos.map((unitInfo) => ({
              originalPrice: unitInfo.originalPrice,
              unitDisplayName: unitInfo.unitDisplayName,
              treatmentMedicineUnitId: unitInfo.treatmentMedicineUnitId,
            })),
          })),
          profileImageUrl: doctor.detail?.profileImageUrl ?? '',
          readyForVideoCall: doctor.readyForVideoCall,
          departments: doctor.departments,
        });
      });
    }),
  };
};

const useSearchNonF2fDoctorsForSeoByUser = (
  request: NonF2fDoctorSeoSearchRequestInput,
  contactType: DiagnosisContactType = DiagnosisContactType.NonF2F
) => {
  const { data } = useInfiniteQuery(
    useSearchNonF2fDoctorsForSeoByUserQuery.getKey({ request, contactType }),
    ({ pageParam }) => {
      return useSearchNonF2fDoctorsForSeoByUserQuery.fetcher({
        request: {
          ...request,
          page: pageParam || 1,
        },
        contactType: contactType,
      })();
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        if (
          !lastPage.searchNonF2fDoctorsForSeoByUser.length ||
          lastPage.searchNonF2fDoctorsForSeoByUser.length < request.limit
        ) {
          return undefined;
        }
        return allPages.length + 1;
      },
      staleTime: 1000 * 60 * 5,
      select: parseSearchNonF2fDoctorsForSeoByUser,
    }
  );

  const nonBenefitDoctors = data?.pages.flatMap((doctor) => doctor) || [];

  return { nonBenefitDoctors };
};

export default useSearchNonF2fDoctorsForSeoByUser;

useSearchNonF2fDoctorsForSeoByUser.prefetchQuery = async (
  queryClient: QueryClient,
  request: NonF2fDoctorSeoSearchRequestInput,
  contactType: DiagnosisContactType = DiagnosisContactType.NonF2F
) => {
  await queryClient.prefetchInfiniteQuery(
    useSearchNonF2fDoctorsForSeoByUserQuery.getKey({ request, contactType }),
    useSearchNonF2fDoctorsForSeoByUserQuery.fetcher({ request, contactType })
  );
};
